import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { EventPanelMetadata } from '@mark43/cad-api';
import { cssVar, VStack } from '@cad-arc';
import { useFormContext, useWatch } from 'react-hook-form';
import { useIntegratedEventDetailsQuery } from '../../common/hooks/use-integrated-event-details-query';
import { GenericObject } from '../../common/types';
import { useEventMetadataStore } from '../../event-panel/event-panel-form/event-metadata';
import { EntityTypes, FrEventFormType, MergedEntityTypes } from '../../fr-entity-panels/types';
import { PersonsOrVehiclesSection } from '../../fr-entity-panels/components/persons-or-vehicles-section';
import { AssociatedEventsSection } from './components/associated-events-section';
import { ReportsSection } from './components/reports-section';
import { InvolvedPropertySection } from '../property-section';
import { useDisplayedReportingEventNumbers } from './hooks';

export function FREventSummaryEntitiesAndReports({
    customStyles,
}: {
    customStyles?: GenericObject<string | number>;
}) {
    const { involvedPersonsAndVehiclesEnabled, associatedEventsEnabled } = useEnabledFeatures();
    const { control } = useFormContext<FrEventFormType>();
    const { eventId } = useEventMetadataStore()?.state?.metadata ?? ({} as EventPanelMetadata);
    const {
        data: eventDetails,
        isInitialLoading: areInitialDetailsLoading,
    } = useIntegratedEventDetailsQuery({ eventId, enabled: !!eventId });
    const enabledFeatures = useEnabledFeatures();
    const displayedReportingEventNumbers = useDisplayedReportingEventNumbers({
        reportingEventNumbers: eventDetails?.reportingEventNumbers,
    });

    const [
        reportingParties,
        mergedReportingParties,
        involvedPersons,
        mergedInvolvedPersons,
        vehicles,
        mergedVehicles,
        towVehicles,
        mergedTowVehicles,
        firearms,
        mergedFirearms,
        drugs,
        mergedDrugs,
        otherItems,
        mergedOtherItems,
    ] = useWatch({
        control,
        name: [
            'reportingParties',
            'mergedReportingParties',
            'involvedPersons',
            'mergedInvolvedPersons',
            'vehicles',
            'mergedVehicles',
            'towVehicles',
            'mergedTowVehicles',
            'firearms',
            'mergedFirearms',
            'drugs',
            'mergedDrugs',
            'otherItems',
            'mergedOtherItems',
        ],
    });

    return (
        <VStack
            w="100%"
            justifyContent="flex-start"
            alignItems="flex-start"
            margin={0}
            gap={`${cssVar('arc.space.6')}`}
            style={{ ...(customStyles ?? {}) }}
            data-testid="fr-summary-and-reports-section"
        >
            {involvedPersonsAndVehiclesEnabled && (
                <PersonsOrVehiclesSection
                    title="Involved Persons"
                    entityType={EntityTypes.INVOLVED_PERSONS}
                    mergedEntityType={MergedEntityTypes.INVOLVED_PERSONS}
                    entitiesList={involvedPersons}
                    mergedEntitiesList={mergedInvolvedPersons}
                />
            )}
            <PersonsOrVehiclesSection
                title="Reporting Parties"
                entityType={EntityTypes.REPORTING_PARTIES}
                mergedEntityType={MergedEntityTypes.REPORTING_PARTIES}
                entitiesList={reportingParties}
                mergedEntitiesList={mergedReportingParties}
            />
            <PersonsOrVehiclesSection
                title="Vehicles"
                entityType={EntityTypes.VEHICLES}
                mergedEntityType={MergedEntityTypes.VEHICLES}
                entitiesList={vehicles}
                mergedEntitiesList={mergedVehicles}
            />
            <PersonsOrVehiclesSection
                title="Tow Vehicles"
                entityType={EntityTypes.TOW_VEHICLES}
                mergedEntityType={MergedEntityTypes.TOW_VEHICLES}
                entitiesList={towVehicles}
                mergedEntitiesList={mergedTowVehicles}
            />
            {enabledFeatures.involvedPropertyEnabled && (
                <InvolvedPropertySection
                    title="Involved Property"
                    eventId={eventId}
                    firearmsList={firearms}
                    mergedFirearmsList={mergedFirearms}
                    drugsList={drugs}
                    mergedDrugsList={mergedDrugs}
                    otherItemsList={otherItems}
                    mergedOtherItemsList={mergedOtherItems}
                />
            )}
            {associatedEventsEnabled && (
                <AssociatedEventsSection
                    associatedEvents={eventDetails?.associatedEvents ?? []}
                    isLoading={areInitialDetailsLoading}
                />
            )}
            <ReportsSection
                isLoading={areInitialDetailsLoading}
                reportingEventNumbers={displayedReportingEventNumbers}
                agencyId={eventDetails?.agencyId}
            />
        </VStack>
    );
}
